import Entity from './Entity';

import _ from 'lodash';

import { stateFieldOptions } from './util';

import Parker from './Parker';
import Invoice from './Invoice';
import CustomerNote from './CustomerNote';


const payorEntityFieldsWithDynamicAllowedValues = {
  type: { enumName: 'PayorTypeEnum' },
  status: { enumName: 'PayorStatusEnum' }
};

const payorEntityFieldsWithStaticAllowedValues = {
  billing_state: {
    options: stateFieldOptions
  },
  info_state: {
    options: stateFieldOptions
  }
};


const billingFields = [
  'billing_firstname',
  'billing_lastname',
  'billing_phone',
  'billing_email',
  'billing_street',
  'billing_city',
  'billing_state',
  'billing_zip',
]

const infoFields = [
  'info_name',
  'info_street',
  'info_city',
  'info_state',
  'info_zip',
]


export default class Payor {
  fields = {
    id: {
      value: null
    },
    readable_id: {
      value: null
    },
    type: {
      value: null
    },
    status: {
      value: null
    },
    contact_notes: {
      value: null
    },
    cognito_sub: {
      value: null
    },
    customer_notes: {
      value: []
    },
    parkers: {
      value: []
    },
    invoices: {
      value: []
    },
    billing_firstname: {
      value: null
    },
    billing_lastname: {
      value: null
    },
    billing_phone: {
      value: null
    },
    billing_work_phone: {
      value: null
    },
    billing_email: {
      value: null
    },
    billing_street: {
      value: null
    },
    billing_city: {
      value: null
    },
    billing_state: {
      value: null
    },
    billing_zip: {
      value: null
    },
    info_name: {
      value: null
    },
    info_attn: {
      value: null
    },
    info_street: {
      value: null
    },
    info_city: {
      value: null
    },
    info_state: {
      value: null
    },
    info_zip: {
      value: null
    },
    created_at: {
      value: null
    },
    updated_at: {
      value: null
    },
    billing_emails: {
      value: null
    }
  };

  billing_contact_different = false;
  new_password = null;


  static getQueryFields() {
    return [
      'id',
      'readable_id',
      'status',
      'type',
      'contact_notes',
      'billing_firstname',
      'billing_lastname',
      'billing_phone',
      'billing_email',
      'billing_street',
      'billing_city',
      'billing_state',
      'billing_zip',
      'info_name',
      'info_attn',
      'info_street',
      'info_city',
      'info_state',
      'info_zip',
      {'customer_notes': [
        'id',
        'text',
        'created_by',
        'created_at'
      ]},
      {'parkers': [
        'id',
        'rate_id',
        'subscription_id',
        'first_name',
        'last_name',
        'email',
        'address',
        'city',
        'state',
        'zip',
        'phone',
        'parking_reason',
        'barcode',
        'group',
        'reserved_space',
        {'access_devices': [
          'device_date_active',
          'device_date_added',
          'device_date_inactive',
          'device_identifier',
          'device_inactive_reason',
          'device_status',
          'type'
        ]},
        {'vehicles': [
          'id',
          'make',
          'model',
          'license',
          'state'
        ]}
      ]},
      {'invoices': [
        'id',
        'subscription_id',
        'readable_id',
        'balance',
        'type',
        {'line_items': [
          'balance',
          'adjusted_gross_amount',
          'created_at',
          {'entries': [
            'type',
            'amount',
            'amount_tax_1',
            'amount_tax_2',
            'amount_tax_3',
            'amount_tax_4',
            'payment_id',
            'created_at'
          ]}
        ]}
      ]}
    ]
  }


  static getFieldAllowedValues(fields) {
    return Entity.getFieldAllowedValues(
      payorEntityFieldsWithStaticAllowedValues,
      payorEntityFieldsWithDynamicAllowedValues,
      fields
    );
  }

  validate(newMode) {
    let fieldsToCheck, copy_to_alternate, copy_to_info;
    if(this.billing_contact_different || !newMode) {
      fieldsToCheck = [...billingFields, ...infoFields];
      copy_to_alternate = false;
      copy_to_info = false;
    }
    else if(this.fields.type.value === 'BUSINESS') {
      fieldsToCheck = [...billingFields, ...infoFields];
      copy_to_alternate = false;
      copy_to_info = false;
    }
    else {
      if(this.fields.info_street.value == null) {
        fieldsToCheck = billingFields;
        copy_to_alternate = false;
        copy_to_info = true;
      }
      else {
        fieldsToCheck = [...billingFields, ...infoFields];
        copy_to_alternate = false;
        copy_to_info = false;
      }
    }

    for(let field of fieldsToCheck) {
      const value = this.fields[field].value;

      if(this.fields[field].value == null) {
        return { valid: false, error: 'Missing required payor information' };
      }

      if(field.includes('email') && !/\S+@\S+\.\S+/.test(value)) {
        return { valid: false, error: 'Invalid email' };
      }
    }

    return { valid: true, copy_to_alternate, copy_to_info };
  }


  setParker(parkerInfo, queryType, rates) {
    const id = (queryType === 'createParker') ? null : parkerInfo.id;
    const index = this.fields.parkers.value.findIndex(parker => parker.fields.id.value === id);

    const parker = Parker.createFromQueryValues(parkerInfo);

    const rate = rates.find(rate => rate.value === parkerInfo.rate_id);
    parker.fields.rate_name.value = rate.label;

    this.fields.parkers.value[index] = parker;
  }


  getQueryFieldValues() {
    const values = _.cloneDeep(PayorInput);

    for (let field in values) {
      switch (field) {
        case 'id':
          if (this.fields[field].value === null) {
            delete values.id;
          }
          else {
            values[field] = this.fields[field].value;
          }
          break;
        case 'billing_emails':
          const newEmails = this.fields[field].value ? this.fields[field].value.filter(email => email.email_id) : []
          const existingEmails = this.fields[field].value ? this.fields[field].value.filter(email => !email.email_id) : []
          const emails = newEmails.map(email => delete email.email_id)
          values[field] = [...existingEmails, emails]
        default:
          values[field] = this.fields[field].value;
      }
    }

    return values;
  }


  updatePayor(values) {
    for(let field in this.fields) {
      if (values.hasOwnProperty(field)) {
        switch(field) {
          case 'parkers':
          case 'customer_notes':
          case 'invoices':
            break;
          default:
            this.fields[field].value = values[field];
        }
      }
    }

    this.billing_contact_different = false;
  }


  static createFromQueryValues(values) {
    let payor = new this();

    for(let field in payor.fields) {
      if (values.hasOwnProperty(field)) {
        switch(field) {
          case 'parkers':
            values.parkers.forEach(parker => {
              payor.fields.parkers.value.push(Parker.createFromQueryValues(parker));
            });
            break;
          case 'customer_notes':
            values.customer_notes.forEach(note => {
              payor.fields.customer_notes.value.push(CustomerNote.createFromQueryValues(note));
            });
            break;
          case 'invoices':
            values.invoices.sort((invoiceA, invoiceB) => {
              const dateA = new Date(parseInt(invoiceA.due_on));
              const formattedA = `${dateA.getFullYear()}-${dateA.getMonth()}-${dateA.getDate()}`;
              const dateB = new Date(parseInt(invoiceB.due_on));
              const formattedB = `${dateB.getFullYear()}-${dateB.getMonth()}-${dateB.getDate()}`;

              return formattedA >= formattedB ? formattedA > formattedB ? 1 : 0 : -1;
            });

            values.invoices.forEach(invoice => {
              invoice.line_items.sort((itemA, itemB) => {
                const dateA = new Date(parseInt(itemA.due_on));
                const formattedA = `${dateA.getFullYear()}-${dateA.getMonth()}-${dateA.getDate()}`;
                const dateB = new Date(parseInt(itemB.due_on));
                const formattedB = `${dateB.getFullYear()}-${dateB.getMonth()}-${dateB.getDate()}`;

                return formattedA >= formattedB ? formattedA > formattedB ? 1 : 0 : -1;
              });
              payor.fields.invoices.value.push(Invoice.createFromQueryValues(invoice));
            });
            break;
          default:
            payor.fields[field].value = values[field];
        }
      }
    }

    payor.billing_contact_different = false;

    return payor;
  }
}


export const PayorInput = {
  id: null,
  type: null,
  status: null,
  contact_notes: null,
  billing_firstname: null,
  billing_lastname: null,
  billing_phone: null,
  billing_work_phone: null,
  billing_email: null,
  billing_emails: null,
  billing_street: null,
  billing_city: null,
  billing_state: null,
  billing_zip: null,
  info_name: null,
  info_attn: null,
  info_street: null,
  info_city: null,
  info_state: null,
  info_zip: null
}
